import type { Ambience } from "./schema";

/**
 * AudioSystem — per-scene background ambience, synthesized with WebAudio so it
 * needs no asset files (real sound, adapted to each scene). Starts on the first
 * user gesture (autoplay policy) and never throws.
 */
export class AudioSystem {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private nodes: AudioNode[] = [];
  private muted = false;
  private currentAmbience: Ambience = "none";

  /** Call from a user gesture (click) to satisfy autoplay policies. */
  resume(): void {
    try {
      if (!this.ctx) {
        const AC = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
        this.ctx = new AC();
        this.master = this.ctx.createGain();
        this.master.gain.value = this.muted ? 0 : 0.18;
        this.master.connect(this.ctx.destination);
      }
      void this.ctx.resume();
      if (this.currentAmbience !== "none") this.setAmbience(this.currentAmbience);
    } catch {
      /* audio unavailable — silent, non-fatal */
    }
  }

  setAmbience(ambience: Ambience): void {
    this.currentAmbience = ambience;
    if (!this.ctx || !this.master) return;
    this.stopNodes();
    if (ambience === "none") return;
    try {
      switch (ambience) {
        case "sea_wind":
        case "desert":
        case "storm":
          this.wind(ambience === "storm" ? 0.9 : ambience === "sea_wind" ? 0.55 : 0.4);
          break;
        case "river":
          this.wind(0.3);
          this.pad([196, 293.66], 0.05);
          break;
        case "temple":
        case "glory":
          this.pad(ambience === "glory" ? [261.63, 329.63, 392.0] : [220, 277.18, 329.63], 0.09);
          break;
        case "garden":
          this.pad([293.66, 440], 0.05);
          this.wind(0.2);
          break;
        case "city":
        case "crowd":
          this.wind(0.25);
          this.pad([146.83, 220], 0.03);
          break;
      }
    } catch {
      /* non-fatal */
    }
  }

  /** Filtered white-noise wind, slowly modulated. */
  private wind(intensity: number): void {
    if (!this.ctx || !this.master) return;
    const ctx = this.ctx;
    const bufferSize = 2 * ctx.sampleRate;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
    const noise = ctx.createBufferSource();
    noise.buffer = buffer;
    noise.loop = true;
    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = 500 + intensity * 400;
    const gain = ctx.createGain();
    gain.gain.value = 0.12 * intensity;
    // slow LFO on the gain to make it breathe
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.08;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 0.05 * intensity;
    lfo.connect(lfoGain).connect(gain.gain);
    noise.connect(filter).connect(gain).connect(this.master);
    noise.start();
    lfo.start();
    this.nodes.push(noise, lfo, filter, gain, lfoGain);
  }

  /** Soft sustained chord pad (sine partials). */
  private pad(freqs: number[], vol: number): void {
    if (!this.ctx || !this.master) return;
    const ctx = this.ctx;
    for (const f of freqs) {
      const osc = ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.value = f;
      const g = ctx.createGain();
      g.gain.value = vol;
      const trem = ctx.createOscillator();
      trem.frequency.value = 0.12;
      const tremGain = ctx.createGain();
      tremGain.gain.value = vol * 0.4;
      trem.connect(tremGain).connect(g.gain);
      osc.connect(g).connect(this.master);
      osc.start();
      trem.start();
      this.nodes.push(osc, trem, g, tremGain);
    }
  }

  toggleMute(): boolean {
    this.muted = !this.muted;
    if (this.master) this.master.gain.value = this.muted ? 0 : 0.18;
    return this.muted;
  }

  private stopNodes(): void {
    for (const n of this.nodes) {
      try {
        (n as OscillatorNode).stop?.();
      } catch {
        /* not a source */
      }
      try {
        n.disconnect();
      } catch {
        /* already gone */
      }
    }
    this.nodes = [];
  }

  dispose(): void {
    this.stopNodes();
    try {
      void this.ctx?.close();
    } catch {
      /* ignore */
    }
    this.ctx = null;
  }
}
